<script lang="ts">
  import { onMount } from 'svelte';
  import { fetchProductions, fetchToday, type ProductionSummary, type TodayPayload } from '../lib/production/api';
  import { getSelectedProductionId, setSelectedProductionId } from '../lib/production/selection';
  import { formatDayLabel, formatDeadline, formatLocalDate, statusLabel } from '../lib/production/format';

  export let onOpenProduction: () => void;

  let data: TodayPayload | null = null;
  let loading = true;
  let loadError = false;
  let stale = false;
  let productions: ProductionSummary[] = [];
  let selectedProductionId = '';

  onMount(() => {
    const handleOnline = () => void load(selectedProductionId || undefined);
    window.addEventListener('online', handleOnline);
    void load();
    return () => window.removeEventListener('online', handleOnline);
  });

  async function load(requestedProductionId?: string) {
    try {
      productions = await fetchProductions();
      const remembered = requestedProductionId ?? (await getSelectedProductionId());
      const accessibleId = productions.some((production) => production.id === remembered)
        ? remembered ?? undefined
        : undefined;
      const result = await fetchToday(accessibleId);
      data = result.data;
      selectedProductionId = result.data.production.id;
      await setSelectedProductionId(selectedProductionId);
      stale = result.stale;
      loadError = false;
    } catch {
      loadError = true;
    } finally {
      loading = false;
    }
  }

  async function changeProduction(event: Event) {
    const productionId = (event.currentTarget as HTMLSelectElement).value;
    if (!productionId || productionId === selectedProductionId) return;
    loading = true;
    await load(productionId);
  }

  function crewLabels(item: NonNullable<TodayPayload['nextUp']>) {
    return item.assignments.map((assignment) => assignment.label ?? assignment.name).join(' · ');
  }
</script>

{#if loading}
  <section class="today-loading" aria-live="polite">
    <span class="eyebrow">OGGI</span>
    <h1>Carico la produzione…</h1>
  </section>
{:else if loadError || !data}
  <section class="today-empty" aria-labelledby="today-error-title">
    <span class="eyebrow">OGGI</span>
    <h1 id="today-error-title">Produzione non disponibile</h1>
    <p>Non trovo una produzione accessibile né una copia recente sul dispositivo.</p>
    <button class="secondary-button" type="button" onclick={() => void load()}>Riprova</button>
  </section>
{:else}
  <section class="today-view" data-testid="today-view">
    <header class="production-heading">
      <div>
        <span class="eyebrow">{data.production.status.replaceAll('_', ' ')}</span>
        <h1>{data.production.title}</h1>
        {#if productions.length > 1}
          <label class="production-switcher">
            <span class="sr-only">Produzione attiva</span>
            <select value={selectedProductionId} onchange={changeProduction}>
              {#each productions as production (production.id)}
                <option value={production.id}>{production.title}</option>
              {/each}
            </select>
          </label>
        {/if}
        {#if data.day}
          <p class="day-line">{formatDayLabel(data.day.dayNumber)} · {formatLocalDate(data.day.date)}</p>
        {:else}
          <p class="day-line">Nessuna giornata per {formatLocalDate(data.projectClock.date)}</p>
        {/if}
      </div>
      {#if stale}
        <span class="offline-copy">COPIA OFFLINE</span>
      {/if}
    </header>

    <div class="timing-strip" aria-label="Orari principali">
      <div class="timing-cell">
        <span>CALL</span>
        <strong>{data.day?.crewCall ?? 'TBD'}</strong>
      </div>
      <div class="timing-cell">
        <span>FIRST SHOT</span>
        <strong>{data.day?.firstShot ?? 'TBD'}</strong>
      </div>
      <div class:timing-cell--warning={data.nextDelivery?.overdue} class="timing-cell timing-cell--delivery">
        <span>{data.nextDelivery?.overdue ? 'DELIVERY IN RITARDO' : 'PROSSIMA DELIVERY'}</span>
        {#if data.nextDelivery}
          <strong>{formatDeadline(data.nextDelivery.deadline).time}</strong>
          <small>{formatDeadline(data.nextDelivery.deadline).date}</small>
        {:else}
          <strong>—</strong>
          <small>Nessuna aperta</small>
        {/if}
      </div>
    </div>

    <button class="day-sheet-button" type="button" onclick={onOpenProduction}>
      APRI GIORNATA
      <span aria-hidden="true">→</span>
    </button>

    <div class="today-columns">
      <div class="today-primary">
        <section class="operational-block next-up-block" aria-labelledby="next-up-title">
          <div class="section-heading-row">
            <h2 id="next-up-title">NEXT UP</h2>
            <span>{data.projectClock.time}</span>
          </div>

          {#if data.nextUp}
            <article class="next-event">
              <div class="event-time">{data.nextUp.startTime}</div>
              <div class="event-copy">
                {#if data.nextUp.location}<span class="event-location">{data.nextUp.location}</span>{/if}
                <h3>{data.nextUp.activity}</h3>
                {#if data.nextUp.assignments.length > 0}
                  <p>{crewLabels(data.nextUp)}</p>
                {/if}
              </div>
              <span class="priority-label" data-priority={data.nextUp.priority}>{data.nextUp.priority}</span>
            </article>

            {#if data.following}
              <div class="following-event">
                <span>DOPO</span>
                <strong>{data.following.startTime} · {data.following.location ?? 'Location TBD'}</strong>
                <p>{data.following.activity}</p>
              </div>
            {/if}
          {:else}
            <div class="compact-empty">
              <strong>Nessun evento con orario confermato.</strong>
              <span>Il Runsheet può contenere voci TBD, ma non vengono promosse a Next Up.</span>
            </div>
          {/if}
        </section>

        <section class="operational-block" aria-labelledby="delivery-title">
          <div class="section-heading-row">
            <h2 id="delivery-title">DAILY DELIVERY</h2>
            {#if data.deliveryFocusDay}
              <span>{formatDayLabel(data.deliveryFocusDay.dayNumber)} · {formatLocalDate(data.deliveryFocusDay.date)}</span>
            {:else}
              <span>PRODUCTION</span>
            {/if}
          </div>

          {#if data.dailyDeliverables.length > 0}
            <div class="delivery-list">
              {#each data.dailyDeliverables as deliverable (deliverable.id)}
                <div class="delivery-row">
                  <div>
                    <strong>{deliverable.type}</strong>
                    <span>{deliverable.title}</span>
                  </div>
                  <span class="delivery-status" data-status={deliverable.status}>{statusLabel(deliverable.status)}</span>
                </div>
              {/each}
            </div>
          {:else}
            <div class="compact-empty">
              <strong>Nessuna delivery associata alla giornata.</strong>
            </div>
          {/if}
        </section>
      </div>

      <aside class="today-secondary">
        <section class="operational-block priorities-block" aria-labelledby="priorities-title">
          <div class="section-heading-row">
            <h2 id="priorities-title">PRIORITÀ</h2>
            <span>{data.priorities.length}</span>
          </div>
          {#if data.priorities.length > 0}
            <ol class="priority-list">
              {#each data.priorities as priority (priority.id)}
                <li>
                  <span class="priority-label" data-priority={priority.priority}>{priority.priority}</span>
                  <strong>{priority.title}</strong>
                </li>
              {/each}
            </ol>
          {:else}
            <div class="compact-empty">
              <strong>Nessuna priorità confermata.</strong>
              <span>Non vengono creati placeholder operativi.</span>
            </div>
          {/if}
        </section>

        <section class="quick-actions" aria-labelledby="quick-actions-title">
          <div class="section-heading-row">
            <h2 id="quick-actions-title">AZIONI RAPIDE</h2>
          </div>
          <div class="quick-grid">
            <button type="button" disabled={!data.capabilities.cameraReport}>Camera Report</button>
            <button type="button" disabled={!data.capabilities.pickup}>Nuovo Pickup</button>
            <button type="button" disabled={!data.capabilities.interview}>Intervista</button>
            <button type="button" disabled={!data.capabilities.equipment}>Equipment</button>
            <button type="button" disabled={!data.capabilities.quickNote}>Nota</button>
          </div>
          <p class="quick-note">Le azioni non ancora implementate restano disabilitate: nessun dato viene simulato.</p>
        </section>
      </aside>
    </div>
  </section>
{/if}

<style>
  .today-view,
  .today-loading,
  .today-empty {
    width: min(100%, var(--content-max));
    margin: 0 auto;
  }

  .today-loading,
  .today-empty {
    padding: var(--space-8) 0;
  }

  .today-loading h1,
  .today-empty h1 {
    margin: var(--space-2) 0;
  }

  .production-heading {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-4);
    padding: var(--space-6) 0 var(--space-5);
    border-bottom: 1px solid var(--border-strong);
  }

  .production-heading h1 {
    margin: var(--space-1) 0 0;
    font-size: clamp(1.75rem, 7vw, 3.1rem);
    line-height: 0.98;
    letter-spacing: -0.045em;
  }

  .production-switcher {
    display: block;
    margin-top: var(--space-3);
  }

  .production-switcher select {
    max-width: 100%;
    min-height: 40px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
    color: var(--text-primary);
    padding: 0 var(--space-3);
    font: inherit;
    font-size: var(--type-sm);
  }

  .day-line {
    margin: var(--space-3) 0 0;
    color: var(--text-secondary);
    font-size: var(--type-sm);
    font-weight: 650;
    letter-spacing: 0.06em;
  }

  .offline-copy {
    flex: none;
    border: 1px solid var(--warning);
    color: var(--warning);
    padding: 5px 7px;
    border-radius: var(--radius-sm);
    font-size: 0.66rem;
    font-weight: 750;
    letter-spacing: 0.08em;
  }

  .timing-strip {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-bottom: 1px solid var(--border-strong);
  }

  .timing-cell {
    min-height: 92px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: var(--space-4) var(--space-3);
    border-right: 1px solid var(--border);
  }

  .timing-cell:nth-child(2) {
    border-right: 0;
  }

  .timing-cell--delivery {
    grid-column: 1 / -1;
    min-height: 78px;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    column-gap: var(--space-3);
    border-top: 1px solid var(--border);
    border-right: 0;
  }

  .timing-cell span,
  .timing-cell small {
    color: var(--text-secondary);
    font-size: 0.68rem;
    font-weight: 750;
    letter-spacing: 0.08em;
  }

  .timing-cell strong {
    font-size: clamp(1.55rem, 7vw, 2.35rem);
    line-height: 1;
    margin-top: var(--space-1);
  }

  .timing-cell--delivery strong {
    grid-row: 1 / span 2;
    grid-column: 2;
    margin: 0;
  }

  .timing-cell--delivery small {
    grid-column: 1;
  }

  .timing-cell--warning strong,
  .timing-cell--warning span {
    color: var(--danger);
  }

  .day-sheet-button {
    width: 100%;
    min-height: 54px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 0;
    border-bottom: 1px solid var(--border-strong);
    background: transparent;
    color: var(--accent-strong);
    padding: 0 var(--space-3);
    font-weight: 760;
    letter-spacing: 0.055em;
  }

  .day-sheet-button span {
    font-size: var(--type-xl);
  }

  .today-columns {
    display: grid;
    gap: var(--space-6);
    padding: var(--space-6) 0 var(--space-10);
  }

  .today-primary,
  .today-secondary {
    display: grid;
    gap: var(--space-6);
    align-content: start;
  }

  .operational-block,
  .quick-actions {
    border-top: 2px solid var(--text-primary);
    padding-top: var(--space-3);
  }

  .section-heading-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }

  .section-heading-row h2 {
    margin: 0;
    font-size: var(--type-sm);
    letter-spacing: 0.08em;
  }

  .section-heading-row > span {
    color: var(--text-secondary);
    font-size: var(--type-xs);
    font-variant-numeric: tabular-nums;
  }

  .next-event {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: var(--space-3);
    align-items: start;
  }

  .event-time {
    font-size: clamp(1.5rem, 8vw, 2.55rem);
    line-height: 0.9;
    font-weight: 760;
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
  }

  .event-location {
    display: block;
    color: var(--accent);
    font-size: var(--type-xs);
    font-weight: 750;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .event-copy h3 {
    margin: var(--space-1) 0;
    font-size: var(--type-lg);
    line-height: 1.12;
  }

  .event-copy p {
    margin: var(--space-2) 0 0;
    color: var(--text-secondary);
    font-size: var(--type-sm);
  }

  .priority-label {
    display: inline-block;
    width: fit-content;
    color: var(--text-secondary);
    font-size: 0.65rem;
    font-weight: 780;
    letter-spacing: 0.075em;
    white-space: nowrap;
  }

  .priority-label[data-priority='MASSIMA'] {
    color: var(--danger);
  }

  .priority-label[data-priority='ALTA'] {
    color: var(--warning);
  }

  .following-event {
    margin-top: var(--space-5);
    padding-top: var(--space-4);
    border-top: 1px solid var(--border);
  }

  .following-event > span {
    display: block;
    margin-bottom: var(--space-1);
    color: var(--text-secondary);
    font-size: 0.65rem;
    font-weight: 750;
    letter-spacing: 0.08em;
  }

  .following-event strong {
    font-size: var(--type-sm);
  }

  .following-event p {
    margin: var(--space-1) 0 0;
    font-size: var(--type-sm);
  }

  .compact-empty {
    display: grid;
    gap: var(--space-1);
    padding: var(--space-2) 0 var(--space-3);
  }

  .compact-empty strong {
    font-size: var(--type-sm);
  }

  .compact-empty span {
    color: var(--text-secondary);
    font-size: var(--type-sm);
    line-height: 1.4;
  }

  .delivery-list {
    border-bottom: 1px solid var(--border);
  }

  .delivery-row {
    min-height: 64px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    border-top: 1px solid var(--border);
  }

  .delivery-row:first-child {
    border-top: 0;
  }

  .delivery-row > div {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  .delivery-row strong {
    font-size: var(--type-sm);
  }

  .delivery-row div span {
    overflow: hidden;
    color: var(--text-secondary);
    font-size: var(--type-xs);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .delivery-status {
    flex: none;
    font-size: 0.65rem;
    font-weight: 780;
    letter-spacing: 0.055em;
  }

  .delivery-status[data-status='DELIVERED'],
  .delivery-status[data-status='APPROVED'] {
    color: var(--success);
  }

  .delivery-status[data-status='CHANGES'] {
    color: var(--warning);
  }

  .priority-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .priority-list li {
    min-height: 52px;
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr);
    align-items: center;
    gap: var(--space-2);
    border-top: 1px solid var(--border);
  }

  .priority-list li:first-child {
    border-top: 0;
  }

  .priority-list strong {
    font-size: var(--type-sm);
  }

  .quick-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1px;
    background: var(--border);
    border: 1px solid var(--border);
  }

  .quick-grid button {
    min-height: 52px;
    border: 0;
    background: var(--surface);
    color: var(--text-primary);
    padding: var(--space-3);
    text-align: left;
    font-size: var(--type-sm);
    font-weight: 680;
  }

  .quick-grid button:disabled {
    color: var(--text-secondary);
    opacity: 0.62;
  }

  .quick-grid button:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .quick-note {
    margin: var(--space-2) 0 0;
    color: var(--text-secondary);
    font-size: 0.72rem;
    line-height: 1.4;
  }

  .secondary-button {
    min-height: 44px;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-sm);
    background: var(--surface);
    padding: 0 var(--space-4);
    font-weight: 650;
  }

  @media (min-width: 700px) {
    .timing-strip {
      grid-template-columns: 1fr 1fr 1.35fr;
    }

    .timing-cell:nth-child(2) {
      border-right: 1px solid var(--border);
    }

    .timing-cell--delivery {
      grid-column: auto;
      border-top: 0;
    }

    .today-columns {
      grid-template-columns: minmax(0, 1.55fr) minmax(250px, 0.8fr);
      gap: var(--space-10);
    }
  }

  @media (min-width: 1000px) {
    .production-heading {
      padding-top: var(--space-8);
    }

    .day-sheet-button {
      padding-left: 0;
      padding-right: 0;
    }
  }
</style>
